import { Component, computed, inject, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { faCircleExclamation, faClose, faInfoCircle, faPlus } from '@fortawesome/pro-regular-svg-icons';

import { BlockModel } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { QuestionPositionData } from '../../../models/positioning/question-position-data.interface';
import { Warning } from '../../../models/positioning/warning.model';
import { QuestionTypeConfig } from '../../../models/question-type-config.constants';
import { QuestionTypeLabels } from '../../../models/question-type-labels.constants';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { SurveyPositioningService } from '../../../services/survey-positioning.service';

@Component({
  selector: 'empathy-move-bulk-to-position',
  templateUrl: 'move-bulk-to-position.component.html',
  styleUrls: ['./move-bulk-to-position.component.scss', './_move-bulk-to-position-theme.component.scss']
})
export class MoveBulkToPositionComponent implements OnInit {
  warnings$ = signal<Warning[]>([]);
  selectedPosition$ = signal<number>(null);

  moveIntoBlock$ = signal<boolean>(null);
  blockSelected$ = signal<BlockModel>(null);
  isBlockBeginning$ = signal<boolean>(null);
  isBlockEnd$ = signal<boolean>(null);

  anyBlocker$ = computed(() => this.warnings$().some((w: Warning) => w.blockerInd));
  selectedQuestion$ = computed(() => this.positionMoved(this.selectedPosition$()));

  questionsToMove: Question[];
  disallowedInsertPositions = new Set<number>();
  questionTypeLabels = QuestionTypeLabels;
  questionsWithBlocks: QuestionPositionData[] = [];
  questionTypeConfig = QuestionTypeConfig;
  readonly faClose = faClose;
  readonly faCircleExclamation = faCircleExclamation;
  readonly faInfoCircle = faInfoCircle;
  readonly faPlus = faPlus;

  surveyEditorService = inject(SurveyEditorService);

  private surveyPositioningService = inject(SurveyPositioningService);

  constructor(
    private dialogRef: MatDialogRef<MoveBulkToPositionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { questionsToMoveIds: string[]; affectedElements: boolean }
  ) {
    this.dialogRef.updateSize('812px', '664px');
  }

  ngOnInit(): void {
    this.questionsWithBlocks = this.getQuestionsWithBlocksData();

    this.questionsToMove = this.data?.questionsToMoveIds.map((id) => this.surveyEditorService.surveyEditor$().questionsIdsWithQuestion.get(id));
    this.disallowedInsertPositions = this.getDisallowedInsertPositions();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onSelectPosition(
    index: number,
    last: boolean,
    moveIntoBlock: boolean = false,
    blockSelected: BlockModel = null,
    isBlockBeginning: boolean = false,
    isBlockEnd: boolean = false
  ): void {
    this.selectedPosition$.set(last ? this.questionsWithBlocks.length : index);
    this.moveIntoBlock$.set(moveIntoBlock);
    if (moveIntoBlock) {
      this.blockSelected$.set(blockSelected);
    } else {
      this.blockSelected$.set(null);
    }
    this.isBlockBeginning$.set(isBlockBeginning);
    this.isBlockEnd$.set(isBlockEnd);

    const newIndex = this.selectedPosition$();
    const allWarnings: Warning[] = [];
    for (const question of this.questionsToMove) {
      allWarnings.push(...this.surveyPositioningService.checkWarnings(newIndex, question.id));
    }
    this.warnings$.set(allWarnings);
  }

  onMove(): void {
    const questions = this.surveyEditorService.surveyEditor$().questions;
    const questionsToMoveIds = new Set(this.questionsToMove.map((question) => question.id));
    const questionsToMoveOrdered = questions.filter((question) => questionsToMoveIds.has(question.id));

    const toTheEnd = this.selectedPosition$() === questions.length;
    const targetIndex = Math.min(this.selectedPosition$(), questions.length - 1);
    let targetQuestionId = toTheEnd ? undefined : questions[targetIndex]?.id;
    const appendToEnd = toTheEnd;
    const insertAfterTarget = this.moveIntoBlock$() && this.isBlockEnd$();

    const questionsToMoveInOrder = appendToEnd || insertAfterTarget ? questionsToMoveOrdered : [...questionsToMoveOrdered].reverse();

    for (const question of questionsToMoveInOrder) {
      const updatedQuestions = this.surveyEditorService.surveyEditor$().questions;
      const newIndex = updatedQuestions.findIndex((q: Question) => q.id === question.id);
      const moved = this.surveyPositioningService.forceMove(
        question.id,
        newIndex,
        targetQuestionId,
        toTheEnd,
        this.moveIntoBlock$(),
        this.blockSelected$(),
        this.isBlockBeginning$(),
        this.isBlockEnd$()
      );
      if (moved === false) {
        return;
      }
      if (!appendToEnd) {
        targetQuestionId = question.id;
      }
    }
    this.dialogRef.close();
  }

  isDisallowedInsertHere(index: number): boolean {
    return this.disallowedInsertPositions.has(index);
  }

  private positionMoved(startIndex: number): Question {
    return this.surveyEditorService.surveyEditor$().questions[startIndex];
  }

  private getQuestionsWithBlocksData(): QuestionPositionData[] {
    return this.surveyEditorService.surveyEditor$().questions.map((question) => {
      const surveyBlocks = this.surveyEditorService.surveyEditor$().blocks.all$();
      const block = this.surveyEditorService.surveyEditor$().blocks.byGroupedQId(question.id);

      const multiBlock = block ? surveyBlocks.find((b) => b.groupedBlockIds.includes(block.id)) : null;

      return {
        block,
        question,
        isFirstElementInBlock: block?.groupedQuestionIds[0] === question.id || false,
        isLastElementInBlock: block?.groupedQuestionIds[block.groupedQuestionIds.length - 1] === question.id || false,
        isFirstBlockInsideMultiBlock: multiBlock?.groupedBlockIds[0] === block?.id || false,
        isInsideMultiBlock: block ? !!surveyBlocks.find((b) => b.groupedBlockIds.includes(block.id)) : false
      };
    });
  }

  private getDisallowedInsertPositions(): Set<number> {
    const positions = new Set<number>();
    this.questionsWithBlocks.forEach((questionData, index) => {
      if (this.data?.questionsToMoveIds.includes(questionData.question.id)) {
        positions.add(index);
        positions.add(index + 1);
      }
    });
    return positions;
  }
}
